import assert from 'assert';
import React, { useEffect, useState } from 'react';
import { gql } from '@soundxyz/gql-string';
import { assertAddress } from '@soundxyz/utils/address';
import { MINTING_INFO_ACTIONS, PILLARS } from '@soundxyz/utils/constants/logging';
import { ContractMethod } from '@soundxyz/utils/constants/transaction';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { styled } from '@components-shared/src/stitches';
import { SellButton } from '@components/CollectButton/BondingCurve/SellButton';
import { TAB_CHOICES } from '@components/Song/MintComponents/MintComponent/BondingCurve/BondingMintComponent';
import {
  LoadingEllipsis,
  StyledTabContent,
} from '@components/Song/MintComponents/MintComponent/BondingCurve/shared';
import { QuantityInput, quantityReducer } from '@components/main-components/inputs/QuantityInput';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { Transaction } from '@context/transaction';
import { SubscriptionEffects } from '@graphql/graphql-ws';
import type { FragmentType } from '@graphql/reactQuery';
import {
  getFragment,
  MinimalReleaseUpdatesDocument,
  ReleaseCollectorUpdateDocument,
} from '@graphql/reactQuery';
import { SamSellInfoFragmentDoc } from '@graphql/reactQuery';
import { useLogInfo } from '@hooks/useLogInfo';
import { useSoundSdk } from '@hooks/useSoundSdk';
import { useTransactionFlow } from '@hooks/useTransactionFlow';
import { trackClickEvent } from '@lib/analytics';
import { useEstimateSamSell } from '@store/sdk/samReactQuery';
import { chainFromId } from '@utils/chain';
import { RE_ENABLE_COLLECT_TIMEOUT, SDK_SOUND_ATTRIBUTON_ID, SPACE } from '@utils/constants';
import { formatWeiToEthForDisplay } from '@utils/helpers';
import { DataTestId } from '@utils/test';
import { NetworkIndicator } from '../../../NetworkIndicator';
import { totalSellPriceWithBuffer } from '../priceUtils';
import { TextLabel } from '../sharedStyles';
import {
  AmountToSellContent,
  MaxThresholdReached,
  NoLiquidityContent,
  NotEnoughEditionsErrorContent,
  UnableToSellErrorContent,
} from './WarningErrorContent';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment SamSellInfo on Release {
    id
    chainId
    contractAddress
    samSellBufferBpsOverride
    samConfig {
      id
      basePrice
      inflectionPoint
      inflectionPrice
    }
    saleDetails {
      soundSwapCollected
    }
  }
`);

const useSellInfo = ({
  data,
  selectedQuantity,
  canSellEditions,
}: {
  data: FragmentType<SamSellInfoFragmentDoc>;
  selectedQuantity: number;
  canSellEditions: boolean;
}) => {
  const { getDeepLink } = useConnectedAuth();
  const { authUserAddress } = useAuth();
  const { soundClientSigner } = useSoundSdk();
  const { safeHandleTransaction } = Transaction.useContainer();
  const [isSelling, setIsSelling] = React.useState(false);
  const logInfo = useLogInfo();

  const {
    id: releaseId,
    chainId,
    contractAddress,
    samConfig,
    samSellBufferBpsOverride,
    saleDetails,
  } = getFragment(SamSellInfoFragmentDoc, data);

  assert(samConfig, 'SamConfig is required for sell info.');

  const { basePrice, inflectionPoint, inflectionPrice } = samConfig;
  const { soundSwapCollected } = saleDetails;

  const sellPriceInWei = totalSellPriceWithBuffer({
    basePrice,
    bufferOverrideBps: samSellBufferBpsOverride,
    currentSupply: soundSwapCollected,
    inflectionPoint,
    inflectionPrice,
    linearPriceSlope: 0n,
    quantity: selectedQuantity,
  });

  const { data: estimateData, refetch: refetchSamSellEstimate } = useEstimateSamSell({
    chainId,
    editionAddress: contractAddress,
    minimumPayout: sellPriceInWei,
    quantity: selectedQuantity,
    canSellEditions,
  });

  const [isSamSellEstimationRevalidating, setIsRevalidatingTokens] = useState(false);

  useEffect(() => {
    const cleanupReleaseUpdates = SubscriptionEffects.onCompleted(
      MinimalReleaseUpdatesDocument,
      ({ result }) => {
        if (result.data.releaseUpdates.id !== releaseId) return;

        refetchSamSellEstimate({
          cancelRefetch: false,
          throwOnError: false,
        });
      },
    );

    const cleanupCollectorsUpdates = SubscriptionEffects.onCompleted(
      ReleaseCollectorUpdateDocument,
      ({ result, variables }) => {
        if (
          variables?.releaseId !== releaseId ||
          result.data.releaseCollectorUpdates.userPublicAddress !== authUserAddress
        ) {
          return;
        }

        setIsRevalidatingTokens(true);
        refetchSamSellEstimate({
          cancelRefetch: false,
          throwOnError: false,
        }).then(() => {
          setIsRevalidatingTokens(false);
        });
      },
    );

    return () => {
      cleanupReleaseUpdates();
      cleanupCollectorsUpdates();
    };
  }, [refetchSamSellEstimate, releaseId, authUserAddress]);

  const transactionFn = useStableCallback(async () => {
    // guarded upstream
    if (!authUserAddress) {
      throw new Error('User is not authenticated');
    }

    const signer = soundClientSigner(chainId);
    assertAddress(contractAddress);
    const { contract, api } = signer.edition.sam({
      editionAddress: contractAddress,
    });

    if (selectedQuantity === 0) {
      throw Error('Select a quantity to collect.');
    }

    let tokenIds = estimateData?.tokenIds;

    if (!tokenIds) {
      const tokensToSell = await api.availableTokensToSell({
        quantity: selectedQuantity,
        ownerPublicAddress: authUserAddress,
      });
      tokenIds = tokensToSell.map((id: number) => BigInt(id));
    }

    const { transactionHash } = await contract.sell({
      tokenIds,
      minimumPayout: sellPriceInWei,
      attributonId: SDK_SOUND_ATTRIBUTON_ID,

      gas: estimateData?.gasEstimate,
      chain: chainFromId(chainId),
    });

    return transactionHash;
  });

  const onSell = useTransactionFlow({
    chainId,
    onChainSwitchFail() {
      setIsSelling(false);
    },
    onAuth: async ({ connectedWalletAddress: walletAddress }) => {
      setIsSelling(true);

      setTimeout(() => {
        setIsSelling(false);
      }, RE_ENABLE_COLLECT_TIMEOUT);

      /**
       * This is critical for a better mobile UX.
       * We have to set before the actual transaction is sent.
       * This way it can open the user's wallet app and make it a seamless flow
       */
      const deepLink = getDeepLink();
      if (deepLink) {
        window.location.href = deepLink;
      }

      logInfo({
        action: MINTING_INFO_ACTIONS.SAM_SELL_EDITION,
        message: 'SAM edition sell initiated',
        data: {
          releaseId,
          quantity: selectedQuantity,
          chainId,
          walletAddress,
        },
        pillar: PILLARS.MINTING,
        isArtistOnlyPage: false,
      });

      await safeHandleTransaction({
        contractMethod: ContractMethod.SAM__SELL_EDITION,
        transactionFn,
        releaseId,
        chainId,
        openProcessingModal: true,
      });

      setIsSelling(false);
    },
  });

  return {
    sellPriceInWei,
    onSell,
    isSelling,
    chainId,
    estimateData,
    isSamSellEstimationRevalidating,
  };
};

export const SellInfo = ({
  data,
  maxLiquidationThreshold,
  editionsOwned,
  hasGoldenEgg,
}: {
  data: FragmentType<SamSellInfoFragmentDoc>;
  /** Maximum amount of NFTs that can be sold to the curve. */
  maxLiquidationThreshold: number;
  /** Number of editions owned by user. This is inclusive of if they own a golden egg. */
  editionsOwned: number;
  /** Does the user own a golden egg? */
  hasGoldenEgg: boolean;
}) => {
  const [hasError, setHasError] = React.useState(false);
  const [state, dispatch] = React.useReducer(quantityReducer, {
    value: 1,
    error: false,
  });

  const { isAuthenticatedWithAPI } = useAuth();
  const { id } = getFragment(SamSellInfoFragmentDoc, data);

  /**
   * Users cannot sell golden eggs.
   * If they have a golden egg, they cannot sell it so we subtract 1 from the number of editions they own.
   */
  const numberOfEditionsUserCanSell = hasGoldenEgg ? editionsOwned - 1 : editionsOwned;
  const unableToSell = numberOfEditionsUserCanSell === 0;

  const { sellPriceInWei, onSell, isSelling, chainId, isSamSellEstimationRevalidating } =
    useSellInfo({
      selectedQuantity: state.value === '' ? 0 : state.value,
      data,
      canSellEditions: numberOfEditionsUserCanSell !== 0,
    });

  const reachedMaxThreshold =
    maxLiquidationThreshold && state.value !== '' && state.value >= maxLiquidationThreshold;

  /**No liquidity */
  const sellingDisabled = maxLiquidationThreshold === 0;
  /** User cannot sell more editions than they have. */
  const notEnoughLiquidity = state.value !== '' && numberOfEditionsUserCanSell < state.value;

  const inputError = isAuthenticatedWithAPI && (unableToSell || notEnoughLiquidity);

  /**
   * we show max threshold warning if:
   * 1. Not Connected - we show the warning if they have reached the max threshold
   * 2. Connected - no errors and they have reached the max threshold
   */
  const showMaxThresholdReached = isAuthenticatedWithAPI
    ? Boolean(reachedMaxThreshold && !(unableToSell || notEnoughLiquidity))
    : Boolean(reachedMaxThreshold);

  const showTotalToSell = isAuthenticatedWithAPI && !notEnoughLiquidity;

  return (
    <StyledTabContent value={TAB_CHOICES.SELL}>
      {!!maxLiquidationThreshold ? (
        <>
          <div
            className="flex min-h-[42px] w-full items-center justify-between py-1"
            data-testid={DataTestId.BONDING_CURVE_QUANTITY_ROW}
          >
            <TextLabel>Quantity</TextLabel>
            <QuantityInput
              state={state}
              dispatch={dispatch}
              max={maxLiquidationThreshold}
              error={inputError}
              onError={error => {
                switch (error) {
                  case 'SET':
                    setHasError(true);
                    break;
                  default:
                    setHasError(false);
                }
              }}
            />
          </div>
          <div
            className="flex min-h-[42px] w-full items-center justify-between py-1"
            data-testid={DataTestId.BONDING_CURVE_TOTAL}
          >
            <TextLabel>
              <h3>Total Price</h3>
            </TextLabel>
            <div className="flex items-center gap-2">
              <NetworkIndicator chainId={chainId} size="md" />
              <div className="text-title-m font-medium leading-[unset]">
                {sellPriceInWei ? (
                  <>
                    {formatWeiToEthForDisplay({ weiValue: sellPriceInWei.toString() })}
                    {SPACE}ETH
                  </>
                ) : (
                  <LoadingWrapper>
                    <LoadingEllipsis />
                    {SPACE}ETH
                  </LoadingWrapper>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <NoLiquidityContent hasNFT={!!editionsOwned} />
      )}
      {isAuthenticatedWithAPI && !sellingDisabled && (
        <>
          {/* If user cannot sell then we want to show error */}
          {unableToSell ? (
            <UnableToSellErrorContent onlyHasGoldenEgg={hasGoldenEgg} />
          ) : (
            // If user can sell then we want to show error if they don't have enough editions to sell
            notEnoughLiquidity && (
              <NotEnoughEditionsErrorContent
                editionCount={numberOfEditionsUserCanSell}
                onClick={() => {
                  dispatch({ type: 'SET_INPUT', payload: numberOfEditionsUserCanSell });
                }}
              />
            )
          )}
        </>
      )}

      {
        // If user can sell then we want to show error if they have reached the max threshold of editions they can sell
        showMaxThresholdReached && <MaxThresholdReached maxAmount={maxLiquidationThreshold} />
      }

      {showTotalToSell && !showMaxThresholdReached && (
        // if user has editions to sell and there is liquidity
        <AmountToSellContent
          editionsOwned={numberOfEditionsUserCanSell}
          maxLiquidationThreshold={maxLiquidationThreshold}
        />
      )}

      <div className="mt-2">
        <Confetti id="withdrawReward" />
        <SellButton
          loading={isSelling || isSamSellEstimationRevalidating}
          disabled={
            !maxLiquidationThreshold ||
            unableToSell ||
            notEnoughLiquidity ||
            hasError ||
            isSamSellEstimationRevalidating
          }
          onSell={() => {
            trackClickEvent({
              event: 'Sell Now Clicked',
              properties: {
                associationType: 'Release',
                associationId: id,
                sourceComponent: 'Mint Component',
                quantity: state.value || 0,
                chainId,
              },
            });
            onSell();
          }}
        />
      </div>
    </StyledTabContent>
  );
};

const LoadingWrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
});

const Confetti = styled('span', {
  left: '50%',
  height: 20,
  bottom: 8,
  width: 20,
  position: 'fixed',
});
